import React, { useState } from 'react';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import { Mail, MapPin, Send, Loader2, AlertCircle } from 'lucide-react';
import { submitContactForm } from '../services/api';

const ContactUs = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg('');

    const res = await submitContactForm(formData);

    setIsSubmitting(false);
    if (res.success) {
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', message: '' });
      }, 3000);
    } else {
      setErrorMsg(res.message || "Something went wrong. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 py-10 md:py-16">
      <SEO
        title="संपर्क करें (Contact Us) | Nath Nagri Bareilly"
        description="नाथ नगरी बरेली के बारे में अधिक जानने के लिए हमसे संपर्क करें। Get in touch with us for inquiries, feedback, or collaborations."
        keywords="contact us, email nathnagri, bareilly temples contact"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 text-center md:text-left">
          <h1 className="text-4xl font-extrabold text-stone-900 mb-4">संपर्क करें (Contact Us)</h1>
          <p className="text-lg text-stone-600 max-w-2xl">
            नाथ नगरी बरेली वेबसाइट पर आने के लिए धन्यवाद। यदि आपके पास कोई सुझाव, प्रश्न या प्रतिक्रिया है, तो बेझिझक हमसे संपर्क करें।
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content (Left) */}
          <div className="flex-1 space-y-8">
            <div className="bg-white p-8 rounded-2xl border border-stone-100 shadow-sm flex flex-col md:flex-row gap-8">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-stone-800 mb-4 flex items-center">
                  <Mail className="w-6 h-6 mr-3 text-saffron-600" /> हमें ईमेल करें
                </h3>
                <p className="text-stone-600 mb-4">
                  किसी भी प्रकार की जानकारी, ब्लॉग पब्लिशिंग, या व्यावसायिक पूछताछ के लिए आप हमें सीधे ईमेल कर सकते हैं:
                </p>
                <a href="mailto:info@nathnagribareilly.com" className="text-xl font-bold text-saffron-600 hover:text-saffron-700 transition">
                  info@nathnagribareilly.com
                </a>
              </div>
              <div className="flex-1 md:border-l md:border-stone-200 md:pl-8">
                <h3 className="text-2xl font-bold text-stone-800 mb-4 flex items-center">
                  <MapPin className="w-6 h-6 mr-3 text-saffron-600" /> हमारा स्थान
                </h3>
                <p className="text-stone-600">
                  बरेली शहर,<br />
                  उत्तर प्रदेश 243001, भारत
                </p>
                <p className="mt-4 text-sm text-stone-500 italic">
                  * हम मुख्य रूप से एक डिजिटल प्लेटफ़ॉर्म हैं। व्यक्तिगत बैठकों के लिए कृपया पहले ईमेल के माध्यम से समय निर्धारित करें।
                </p>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border border-stone-100 shadow-sm">
              <h3 className="text-2xl font-bold text-stone-800 mb-6">हमें एक संदेश भेजें</h3>

              {submitted ? (
                <div className="bg-green-50 border border-green-200 text-green-700 p-6 rounded-xl flex flex-col items-center justify-center text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Send className="w-6 h-6 text-green-600" />
                  </div>
                  <h4 className="text-xl font-bold mb-2">संदेश प्राप्त हुआ! (Sent successfully)</h4>
                  <p>हमारा संपर्क करने के लिए धन्यवाद। हम जल्द ही आपसे संपर्क करेंगे।</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6 flex flex-col">
                  {errorMsg && (
                    <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl flex items-center">
                      <AlertCircle className="w-5 h-5 mr-3" />
                      <p className="text-sm font-medium">{errorMsg}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">आपका नाम (Name) *</label>
                      <input
                        type="text" required
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        value={formData.name}
                        className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                        placeholder="शुभ नाम दर्ज करें"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">ईमेल (Email) *</label>
                      <input
                        type="email" required
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        value={formData.email}
                        className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-saffron-500"
                        placeholder="email@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">आपका संदेश (Message) *</label>
                    <textarea
                      required rows="5"
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      value={formData.message}
                      className="w-full bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-saffron-500 resize-none"
                      placeholder="आप क्या कहना चाहते हैं?"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="self-start px-8 py-3 bg-stone-800 text-white font-bold rounded-xl hover:bg-black transition-colors focus:ring-4 focus:ring-stone-200 disabled:bg-stone-400 disabled:cursor-not-allowed flex items-center"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> भेजा जा रहा है...</>
                    ) : (
                      'सबमिट करें (Submit)'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Sidebar (Right) */}
          <div className="w-full lg:w-80 flex-shrink-0">
            <AdBanner placement="sidebar" pageName="contact" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
